import React, { useState } from 'react';
import arrowUp from '../../assets/images/chevron-up.svg';
import arrowDown from '../../assets/images/chevron-down.svg';
import Card from '../Card/Card';
import { BolderText } from '../BolderText/BolderText';
import IconButton from '../IconButton/IconButton';
import { Icon } from '../Icon/Icon';
import { ReviewablePendingBookDto } from '../../api/MeetingsAPI';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import { useTranslation } from 'react-i18next';
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import { Copy, Check, CreditCard } from 'lucide-react';
import { toastService } from '../../services/ToastService';
import MeetingsAPI from '../../api/MeetingsAPI';

interface PendingRefundingCardProps {
  meeting: ReviewablePendingBookDto;
  onRefundProcessed: (id: number) => void;
}

const PendingRefundingCard: React.FC<PendingRefundingCardProps> = ({ meeting, onRefundProcessed }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleRefundAction = async (action: 'process' | 'reject') => {
    setIsLoading(true);
    try {
      if (action === 'process') {
        await MeetingsAPI.processRefund(meeting.id);
        toastService.showToast(
          t("booking.pending.refund_processed") || "Refund has been processed", 
          "success"
        );
      } else {
        await MeetingsAPI.rejectRefund(meeting.id);
        toastService.showToast(
          t("booking.pending.refund_rejected") || "Refund has been rejected", 
          "success"
        );
      }
      onRefundProcessed(meeting.id);
    } catch (error) {
      console.error(`Error ${action === 'process' ? 'processing' : 'rejecting'} refund:`, error);
      toastService.showToast(
        t(`booking.pending.${action === 'process' ? 'process_refund_error' : 'reject_refund_error'}`) || 
        `Failed to ${action === 'process' ? 'process' : 'reject'} refund`, 
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const copyLinkToClipboard = () => {
    const fullLink = `${window.location.origin}${meeting.trackingLink}`;
    navigator.clipboard.writeText(fullLink)
      .then(() => {
        setCopied(true);
        toastService.showToast(t("booking.pending.link_copied") || "Link copied!");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        toastService.showToast(t("booking.pending.link_error") || "Copy failed", "error");
      });
  };

  return (
    <Card>
      <>
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <BolderText text={meeting.customerName} />
            {meeting.refundAmount && (
              <div className="flex items-center text-gray-700 font-medium mt-1">
                <CreditCard className="h-4 w-4 mr-1" />
                <span>{t("booking.pending.refund_amount") || "Refund Amount"}: {meeting.refundAmount}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ButtonV2
              variant="outline"
              size="sm"
              className="h-8 flex items-center gap-1"
              onClick={copyLinkToClipboard}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="hidden sm:inline">{t("booking.pending.copy_link") || "Copy Link"}</span>
            </ButtonV2>
          </div>
        </div>
        <div className="text-sm text-gray-600">{meeting.meetingType}</div>
        <div className="text-sm text-gray-500">{meeting.date} at {meeting.time}</div>
        <div className="flex justify-end">
          <IconButton onClick={() => setIsExpanded(!isExpanded)} >
            <Icon url={isExpanded ? arrowUp : arrowDown} cssStyles="justify-end"/>
          </IconButton>
        </div>

        {isExpanded && (
          <div className="border-l-2 border-gray-200 pl-1">
            <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.email")}:</strong> {meeting.customerData?.email}</div>
            <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.phone")}:</strong> {meeting.customerData?.phone}</div>
            {meeting.customerData?.budgetRange && (
              <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.budget")}:</strong> {meeting.customerData?.budgetRange}</div>
            )}
            {meeting.customerData?.numberOfGuests && (
                <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.people")}:</strong> {meeting.customerData?.numberOfGuests}</div>
            )}
            {meeting.customerData?.size && (
              <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.size")}:</strong> {meeting.customerData?.size}</div>
            )}
            {meeting.customerData?.weddingDate && (
                <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.wedding_date")}:</strong> {meeting.customerData?.weddingDate}</div>
            )}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <PrimaryButton 
            text={isLoading ? t("booking.pending.processing")! : t("booking.pending.process_refund")!}
            fn={() => handleRefundAction('process')}
            isDisabled={isLoading}
          />
          <SecondaryButton 
            text={isLoading ? t("booking.pending.processing")! : t("booking.pending.decline_refund")!}
            fn={() => handleRefundAction('reject')}
            isDisabled={isLoading}
          />
        </div>
      </>
    </Card>
  );
};

export default PendingRefundingCard; 